'use client';
import { useState, useCallback } from 'react';
import { appStorage } from '../utils/appStorage';
import { getDebugInfo as getFontSizeDebugInfo, calculateScreenSizes } from '../utils/fontSizeCalculator';

/**
 * 依頼関連調査: ページ上の主要要素のスタイル情報を収集する関数群
 * 調査内容を変更したい場合はこの配列を編集する
 */
const investigationScripts = [
  {
    label: '脚注フォントサイズ計算（詳細）',
    fn: () => {
      const info = getFontSizeDebugInfo();
      if (!info) return 'no window';
      const liveSizes = {};
      for (const screen of Object.keys(info.cardHeightVh)) {
        liveSizes[screen] = calculateScreenSizes(info.innerWidth, info.innerHeight, screen);
      }
      return Object.assign({}, info, { liveCalculatedSizes: liveSizes });
    },
  },

  {
    label: 'レイアウト要素のスタイル',
    fn: () => {
      const targets = document.querySelectorAll('[data-se-section]');
      const results = [];
      targets.forEach(el => {
        const cs = getComputedStyle(el);
        const rect = el.getBoundingClientRect();
        results.push({
          section: el.getAttribute('data-se-section'),
          tag: el.tagName,
          className: el.className,
          style: el.getAttribute('style') || '(none)',
          computed: {
            width: cs.width, height: cs.height,
            fontSize: cs.fontSize, lineHeight: cs.lineHeight,
            padding: cs.padding, margin: cs.margin,
            position: cs.position, display: cs.display,
            flexDirection: cs.flexDirection, alignItems: cs.alignItems, justifyContent: cs.justifyContent,
          },
          rect: { width: Math.round(rect.width), height: Math.round(rect.height), top: Math.round(rect.top), left: Math.round(rect.left) },
        });
      });
      return results;
    },
  },
  {
    label: '背景付きコンテナ(白枠等)',
    fn: () => {
      const all = document.querySelectorAll('div');
      const results = [];
      all.forEach(el => {
        const cs = getComputedStyle(el);
        const rect = el.getBoundingClientRect();
        const hasBorder = cs.borderWidth && cs.borderWidth !== '0px';
        const hasShadow = cs.boxShadow && cs.boxShadow !== 'none';
        if ((hasBorder || hasShadow) && rect.height > 80 && rect.width > 100) {
          results.push({
            className: el.className.substring(0, 120),
            style: el.getAttribute('style') || '(none)',
            computed: {
              width: cs.width, height: cs.height, minHeight: cs.minHeight, maxHeight: cs.maxHeight,
              backgroundColor: cs.backgroundColor, border: cs.border, boxShadow: cs.boxShadow.substring(0, 60),
              padding: cs.padding, overflow: cs.overflow, position: cs.position,
            },
            rect: { width: Math.round(rect.width), height: Math.round(rect.height) },
            children: el.children.length,
          });
        }
      });
      return results;
    },
  },
  {
    label: 'タイトル・見出し要素',
    fn: () => {
      const selectors = 'h1, h2, h3, [class*="title"], [class*="font-bold"][class*="text-"]';
      const els = document.querySelectorAll(selectors);
      const results = [];
      const seen = new Set();
      els.forEach(el => {
        const text = (el.textContent || '').trim().substring(0, 40);
        if (!text || seen.has(text)) return;
        seen.add(text);
        const cs = getComputedStyle(el);
        results.push({
          text,
          tag: el.tagName,
          className: el.className.substring(0, 120),
          style: el.getAttribute('style') || '(none)',
          computed: { fontSize: cs.fontSize, fontWeight: cs.fontWeight, letterSpacing: cs.letterSpacing, lineHeight: cs.lineHeight, color: cs.color, whiteSpace: cs.whiteSpace },
        });
      });
      return results;
    },
  },
  {
    label: 'ボタン要素',
    fn: () => {
      const btns = document.querySelectorAll('button');
      const results = [];
      btns.forEach(el => {
        const text = (el.textContent || '').trim().substring(0, 30);
        if (!text) return;
        const cs = getComputedStyle(el);
        const rect = el.getBoundingClientRect();
        results.push({
          text,
          className: el.className.substring(0, 120),
          style: el.getAttribute('style') || '(none)',
          computed: { fontSize: cs.fontSize, width: cs.width, height: cs.height, padding: cs.padding, backgroundColor: cs.backgroundColor },
          rect: { width: Math.round(rect.width), height: Math.round(rect.height) },
        });
      });
      return results;
    },
  },
  {
    label: '\u9577\u6587Talk\u8A9E\u5F59\u30C7\u30FC\u30BF\u8ABF\u67FB',
    fn: () => {
      // listening/page.jsで使われるvocabularyデータをwindowから探す
      const lrSubdir = sessionStorage.getItem('lr_subdir') || '(not set)';
      const appName = sessionStorage.getItem('app_name') || '(not set)';
      // __NEXT_DATA__やReact Fiberからデータを探す
      const results = { lr_subdir: lrSubdir, app_name: appName };
      // カード内のテキスト要素を収集
      const textEls = document.querySelectorAll('[class*="font-bold"][class*="text-["]');
      const cardTexts = [];
      textEls.forEach(el => {
        const text = (el.textContent || '').trim();
        if (text && text.length > 2 && text.length < 200) {
          const cs = getComputedStyle(el);
          cardTexts.push({ text: text.substring(0, 80), color: cs.color, fontSize: cs.fontSize });
        }
      });
      results.cardTexts = cardTexts.slice(0, 20);
      // フェッチでAPIから直接データを取得して確認
      const appId = sessionStorage.getItem('app_id');
      const cookies = document.cookie;
      const courseMatch = cookies.match(/course=([^;]+)/);
      const chapterIdMatch = cookies.match(/chapter_id=([^;]+)/);
      results.appId = appId || '(not set)';
      results.courseCookie = courseMatch ? decodeURIComponent(courseMatch[1]).substring(0, 100) : '(not set)';
      results.chapterIdCookie = chapterIdMatch ? chapterIdMatch[1] : '(not set)';
      return results;
    },
  },
];

/**
 * 開発環境用デバッグ情報表示ボタン（バージョンがd始まりの場合のみ表示）
 * 画面下部中央に丸いアイコンを配置し、タップでストレージ情報をモーダル表示
 * debug=1のDebugStoragePanelとは別機能
 */
export default function DevDebugButton() {
  const [showDebug, setShowDebug] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const isDev = (process.env.NEXT_PUBLIC_APP_VERSION || '').startsWith('d');

  const getDebugInfo = useCallback(() => {
    if (typeof window === 'undefined') return {};
    const params = new URLSearchParams(window.location.search);
    return {
      'URL': window.location.href,
      'Cookie(UserEmail)': (() => { try { const m = document.cookie.match(/(^| )user_email=([^;]+)/); return m ? m[2] : '(なし)'; } catch(e) { return 'error'; } })(),
      'localStorage(saved_user_email)': (() => { try { return localStorage.getItem('saved_user_email') || '(なし)'; } catch(e) { return 'error'; } })(),
      'URLParam(email)': params.get('email') || '(なし)',
      'URLParam(reason)': params.get('reason') || '(なし)',
      'URLParam(app)': params.get('app') || '(なし)',
      'Cookie(app_version)': (() => { try { const m = document.cookie.match(/(^| )app_version=([^;]+)/); return m ? m[2] : '(なし)'; } catch(e) { return 'error'; } })(),
      'localStorage(app_version)': (() => { try { return localStorage.getItem('app_version') || '(なし)'; } catch(e) { return 'error'; } })(),
      'sessionStorage(app_id)': (() => { try { return sessionStorage.getItem('app_id') || '(なし)'; } catch(e) { return 'error'; } })(),
      'sessionStorage(app_name)': (() => { try { return sessionStorage.getItem('app_name') || '(なし)'; } catch(e) { return 'error'; } })(),
      'appStorage.appName': (() => { try { return appStorage.getAppName() || '(なし)'; } catch(e) { return 'error'; } })(),
      'appStorage.appTitle': (() => { try { return appStorage.getAppTitle() || '(なし)'; } catch(e) { return 'error'; } })(),
      'sessionStorage(lr_subdir)': (() => { try { return sessionStorage.getItem('lr_subdir') || '(なし)'; } catch(e) { return 'error'; } })(),
      'APP_VERSION(env)': process.env.NEXT_PUBLIC_APP_VERSION || '(なし)',
      'AllCookies': (() => { try { return document.cookie || '(なし)'; } catch(e) { return 'error'; } })(),
    };
  }, []);

  const handleCopy = useCallback(() => {
    const info = getDebugInfo();
    const text = Object.entries(info).map(([k, v]) => k + ': ' + v).join('\n');
    navigator.clipboard.writeText(text).then(() => alert('コピーしました'));
  }, [getDebugInfo]);

  const handleInvestigation = useCallback((idx) => {
    try {
      const script = investigationScripts[idx];
      const result = script.fn();
      const text = '=== ' + script.label + ' === ' + new Date().toLocaleString() + '\nURL: ' + location.href + '\nviewport: ' + window.innerWidth + 'x' + window.innerHeight + '\n\n' + JSON.stringify(result, null, 2);
      navigator.clipboard.writeText(text).then(() => {
        setCopiedIdx(idx);
        setTimeout(() => setCopiedIdx(null), 2000);
      });
    } catch (e) {
      navigator.clipboard.writeText('Error: ' + e.message);
    }
  }, []);

  const handleAllInvestigation = useCallback(() => {
    try {
      const lines = ['=== 依頼関連調査（全項目） === ' + new Date().toLocaleString(), 'URL: ' + location.href, 'viewport: ' + window.innerWidth + 'x' + window.innerHeight, ''];
      for (const script of investigationScripts) {
        lines.push('--- ' + script.label + ' ---');
        lines.push(JSON.stringify(script.fn(), null, 2));
        lines.push('');
      }
      navigator.clipboard.writeText(lines.join('\n')).then(() => {
        setCopiedIdx('all');
        setTimeout(() => setCopiedIdx(null), 2000);
      });
    } catch (e) {
      navigator.clipboard.writeText('Error: ' + e.message);
    }
  }, []);

  if (!isDev) return null;

  return (
    <>
      <div
        onClick={() => setShowDebug(true)}
        style={{
          position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
          width: '36px', height: '36px', borderRadius: '50%',
          backgroundColor: '#666', color: '#fff', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          fontSize: '16px', zIndex: 9999, cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)', opacity: 0.6,
        }}
      >
        D
      </div>

      {showDebug && (
        <div
          onClick={() => setShowDebug(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#fff', borderRadius: '12px', padding: '16px',
              maxWidth: '90vw', maxHeight: '80vh', overflow: 'auto',
              fontSize: '12px', textAlign: 'left', wordBreak: 'break-all',
            }}
          >
            <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>Dev Debug Info</div>
            {Object.entries(getDebugInfo()).map(([k, v]) => (
              <div key={k} style={{ marginBottom: '3px', borderBottom: '1px solid #eee', paddingBottom: '3px' }}>
                <span style={{ color: '#888' }}>{k}:</span><br />{v}
              </div>
            ))}

            {/* ★ 脚注フォントサイズ計算セクション */}
            <div style={{ marginTop: '16px', borderTop: '2px solid #ff9800', paddingTop: '12px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px', color: '#e65100' }}>脚注フォントサイズ計算式</div>
              {(() => {
                try {
                  const info = getFontSizeDebugInfo();
                  if (!info) return <div style={{ color: '#888' }}>window未準備</div>;
                  return (
                    <div style={{ fontSize: '11px', lineHeight: 1.5 }}>
                      <div><b>ビューポート:</b> {info.innerWidth} x {info.innerHeight}</div>
                      <div><b>sizeKey:</b> {info.sizeKey}</div>
                      <div><b>cacheKey:</b> {info.cacheKey}</div>
                      <div><b>isDesktopLayout(sm+):</b> {String(info.isDesktopLayout)}</div>
                      <div style={{ marginTop: '6px' }}><b>計算式:</b></div>
                      <div style={{ paddingLeft: '8px', color: '#555' }}>
                        {info.formula}
                      </div>
                      <div style={{ marginTop: '6px' }}><b>スキーマver:</b> v{info.schemaVersion}</div>
                      <div style={{ marginTop: '6px' }}><b>画面ごとのカード高さ(vh)と要素サイズ(px):</b></div>
                      <div style={{ overflowX: 'auto', marginTop: '4px' }}>
                        <table style={{ borderCollapse: 'collapse', fontSize: '10px', width: '100%' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#fff3e0' }}>
                              <th style={{ border: '1px solid #ccc', padding: '2px 4px', textAlign: 'left' }}>画面</th>
                              <th style={{ border: '1px solid #ccc', padding: '2px 4px' }}>cardVh<br/>(dsk/mob)</th>
                              {(() => {
                                const ratios = info.isDesktopLayout ? info.elementRatioDesktop : info.elementRatioMobile;
                                return Object.keys(ratios).map(k => (
                                  <th key={k} style={{ border: '1px solid #ccc', padding: '2px 4px' }}>{k}<br/>{(ratios[k]*100).toFixed(2)}%</th>
                                ));
                              })()}
                            </tr>
                          </thead>
                          <tbody>
                            {Object.keys(info.cardHeightVh).map(screen => {
                              const sizes = calculateScreenSizes(info.innerWidth, info.innerHeight, screen);
                              const card = info.cardHeightVh[screen];
                              const ratios = info.isDesktopLayout ? info.elementRatioDesktop : info.elementRatioMobile;
                              return (
                                <tr key={screen}>
                                  <td style={{ border: '1px solid #ccc', padding: '2px 4px' }}>{screen}</td>
                                  <td style={{ border: '1px solid #ccc', padding: '2px 4px', textAlign: 'right' }}>{card.desktop}/{card.mobile}</td>
                                  {Object.keys(ratios).map(k => (
                                    <td key={k} style={{ border: '1px solid #ccc', padding: '2px 4px', textAlign: 'right' }}>{sizes ? sizes[k] : '-'}</td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ marginTop: '8px' }}>
                        <b>localStorageキャッシュ:</b>{' '}
                        {info.cached ? <span style={{ color: '#2e7d32' }}>あり ({info.cached.calculatedAt})</span> : <span style={{ color: '#c62828' }}>なし</span>}
                      </div>
                    </div>
                  );
                } catch (e) {
                  return <div style={{ color: '#c62828' }}>error: {String(e)}</div>;
                }
              })()}
            </div>

            {/* 依頼関連調査セクション */}
            <div style={{ marginTop: '16px', borderTop: '2px solid #4fc3f7', paddingTop: '12px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px', color: '#1976d2' }}>依頼関連調査</div>
              <div style={{ color: '#888', fontSize: '11px', marginBottom: '8px' }}>ボタンを押すと調査を実行し結果をクリップボードにコピーします</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {investigationScripts.map((script, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleInvestigation(idx)}
                    style={{
                      padding: '8px 12px', backgroundColor: copiedIdx === idx ? '#4caf50' : '#1976d2', color: '#fff',
                      border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    {copiedIdx === idx ? '✓ コピーしました' : script.label}
                  </button>
                ))}
                <button
                  onClick={handleAllInvestigation}
                  style={{
                    padding: '8px 12px', backgroundColor: copiedIdx === 'all' ? '#4caf50' : '#333', color: '#fff',
                    border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold',
                  }}
                >
                  {copiedIdx === 'all' ? '✓ コピーしました' : '全項目まとめてコピー'}
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button
                onClick={handleCopy}
                style={{ flex: 1, padding: '8px', backgroundColor: '#333', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px' }}
              >
                デバッグ情報コピー
              </button>
              <button
                onClick={() => setShowDebug(false)}
                style={{ flex: 1, padding: '8px', backgroundColor: '#ccc', color: '#333', border: 'none', borderRadius: '6px', fontSize: '13px' }}
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
